import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from './ui/Card';
export function EvidenceSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="evidence" className="py-24 bg-slate-900 text-white overflow-hidden" data-id="element-1">
      <div className="container mx-auto px-4" data-id="element-2">
        <div className="mb-16" data-id="element-3">
          <motion.span initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-teal-400 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-4">
            Clinical Results
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold mb-6" data-id="element-5">
            The Data Backs It Up
          </motion.h2>
          <p className="text-slate-400 max-w-2xl text-lg" data-id="element-6">
            In head-to-head Phase III trials, Vabysmo demonstrated superior
            durability and extended treatment intervals compared to aflibercept.
          </p>
        </div>

        <div ref={ref} className="grid md:grid-cols-2 gap-8 lg:gap-12" data-id="element-7">
          {/* Duration Chart */}
          <Card className="bg-slate-800 border-slate-700 p-8" data-id="element-8">
            <div className="flex justify-between items-start mb-8" data-id="element-9">
              <div data-id="element-10">
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-11">
                  Median Treatment Durability
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-12">Time to next injection</p>
              </div>
              <div className="bg-teal-500/10 text-teal-400 px-3 py-1 rounded-full font-bold text-sm border border-teal-500/20" data-id="element-13">
                2x Longer
              </div>
            </div>

            <div className="space-y-6" data-id="element-14">
              {/* Vabysmo Bar */}
              <div data-id="element-15">
                <div className="flex justify-between text-sm mb-2" data-id="element-16">
                  <span className="font-bold text-white" data-id="element-17">Vabysmo</span>
                  <span className="font-bold text-teal-400" data-id="element-18">18.4 months</span>
                </div>
                <div className="h-12 bg-slate-700/50 rounded-lg overflow-hidden relative" data-id="element-19">
                  <motion.div className="absolute top-0 left-0 h-full bg-gradient-to-r from-teal-600 to-teal-400" initial={{
                  width: 0
                }} animate={isInView ? {
                  width: '100%'
                } : {
                  width: 0
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut'
                }} data-id="element-20" />
                </div>
              </div>

              {/* Comparator Bar */}
              <div data-id="element-21">
                <div className="flex justify-between text-sm mb-2" data-id="element-22">
                  <span className="font-medium text-slate-400" data-id="element-23">
                    Aflibercept
                  </span>
                  <span className="font-medium text-slate-400" data-id="element-24">9.6 months</span>
                </div>
                <div className="h-12 bg-slate-700/50 rounded-lg overflow-hidden relative" data-id="element-25">
                  <motion.div className="absolute top-0 left-0 h-full bg-slate-500" initial={{
                  width: 0
                }} animate={isInView ? {
                  width: '52%'
                } : {
                  width: 0
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut',
                  delay: 0.2
                }} data-id="element-26" />
                </div>
              </div>
            </div>
          </Card>

          {/* Response Rate Chart */}
          <Card className="bg-slate-800 border-slate-700 p-8" data-id="element-27">
            <div className="flex justify-between items-start mb-8" data-id="element-28">
              <div data-id="element-29">
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-30">
                  Durable Response Rate
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-31">
                  At 1 year (Q12W or Q16W)
                </p>
              </div>
              <div className="bg-blue-500/10 text-blue-400 px-3 py-1 rounded-full font-bold text-sm border border-blue-500/20" data-id="element-32">
                2.3x Higher
              </div>
            </div>

            <div className="space-y-6" data-id="element-33">
              {/* Vabysmo Bar */}
              <div data-id="element-34">
                <div className="flex justify-between text-sm mb-2" data-id="element-35">
                  <span className="font-bold text-white" data-id="element-36">Vabysmo</span>
                  <span className="font-bold text-blue-400" data-id="element-37">58.4%</span>
                </div>
                <div className="h-12 bg-slate-700/50 rounded-lg overflow-hidden relative" data-id="element-38">
                  <motion.div className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-600 to-blue-400" initial={{
                  width: 0
                }} animate={isInView ? {
                  width: '58.4%'
                } : {
                  width: 0
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut',
                  delay: 0.4
                }} data-id="element-39" />
                </div>
              </div>

              {/* Comparator Bar */}
              <div data-id="element-40">
                <div className="flex justify-between text-sm mb-2" data-id="element-41">
                  <span className="font-medium text-slate-400" data-id="element-42">
                    Aflibercept
                  </span>
                  <span className="font-medium text-slate-400" data-id="element-43">25%</span>
                </div>
                <div className="h-12 bg-slate-700/50 rounded-lg overflow-hidden relative" data-id="element-44">
                  <motion.div className="absolute top-0 left-0 h-full bg-slate-500" initial={{
                  width: 0
                }} animate={isInView ? {
                  width: '25%'
                } : {
                  width: 0
                }} transition={{
                  duration: 1.5,
                  ease: 'easeOut',
                  delay: 0.6
                }} data-id="element-45" />
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 text-center" data-id="element-46">
          <p className="text-slate-500 text-sm" data-id="element-47">
            Data from pivotal Phase III trials (TENAYA and LUCERNE). Individual
            results may vary. Comparisons are descriptive and based on trial
            data.
          </p>
        </div>
      </div>
    </section>;
}